import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import {
  Calendar as CalendarIcon,
  Filter,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Clock,
  Banknote,
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Plus,
  Hash,
  Pencil,
  Trash2,
  RotateCcw,
} from 'lucide-react';
import { useAppStore } from '../store';
import { useViewMode } from '../contexts/ViewModeContext';
import BookingHint from '../components/BookingHint';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { Booking } from '../types';
import ReactMarkdown from 'react-markdown';
import { getSeasonForDate, getWeeklyRate } from '../utils/seasonUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';

moment.locale('de');
moment.updateLocale('de', { week: { dow: 1, doy: 4 } });
const localizer = momentLocalizer(moment);

const FamilyInfoPanel: React.FC<{ info: string }> = ({ info }) => {
  const [open, setOpen] = useState(false);
  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors text-left"
      >
        <span className="font-semibold text-amber-800">Infos für euren Aufenthalt</span>
        <ChevronDown size={18} className={`text-amber-600 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 px-5 py-4 rounded-xl border border-amber-200 bg-amber-50 prose prose-sm max-w-none text-amber-900">
          <ReactMarkdown>{info}</ReactMarkdown>
        </div>
      )}
    </section>
  );
};

const BookingsPage: React.FC = () => {
  const { bookings, homes, users, currentUser, updateBooking, cancelBooking, deleteBooking, createBooking, checkAvailability, editBooking, familyBookingInfo } = useAppStore();
  const { isGuestView } = useViewMode();
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'cancelled'>('all');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState('');
  const [selectedEndDate, setSelectedEndDate] = useState('');
  const [isSelectingRange, setIsSelectingRange] = useState(false);
  const [selectedHomes, setSelectedHomes] = useState<string[]>([]);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState<{ available: boolean; message?: string } | null>(null);
  const [otherHomeAvailability, setOtherHomeAvailability] = useState<{ available: boolean; message?: string } | null>(null);
  const [homeDropdownOpen, setHomeDropdownOpen] = useState(false);
  const homeDropdownRef = useRef<HTMLDivElement>(null);
  const [cleaningOption, setCleaningOption] = useState<'self' | 'service'>('self');
  const [showFamilyInfo, setShowFamilyInfo] = useState(false);

  // Derived from selectedHomes
  const selectedHome = selectedHomes[0] || '';
  const bookBothHomes = selectedHomes.length > 1;
  const isOwner = Boolean(currentUser?.isAdmin || currentUser?.isFamily);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Delete modal state
  const [deletingBooking, setDeletingBooking] = useState<Booking | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [priceOverrides, setPriceOverrides] = useState<Record<string, string>>({});

  // Edit modal state
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editAvailabilityStatus, setEditAvailabilityStatus] = useState<{ available: boolean; message?: string } | null>(null);

  // Calendar shows only non-cancelled bookings
  const calendarFilteredBookings = useMemo(() => {
    let filtered = bookings.filter(booking => booking.status !== 'cancelled');

    if (filter !== 'all' && filter !== 'cancelled') {
      filtered = filtered.filter(booking => booking.status === filter);
    }

    return filtered.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [bookings, filter]);

  // Table shows only user's bookings in guest view
  const tableFilteredBookings = useMemo(() => {
    let filtered = bookings;

    // In guest view, only show current user's bookings
    if (isGuestView && currentUser) {
      filtered = filtered.filter(booking => booking.userId === currentUser.id);
    }

    if (filter !== 'all') {
      filtered = filtered.filter(booking => booking.status === filter);
    }

    return filtered.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
  }, [bookings, filter, isGuestView, currentUser]);

  // Get base bookings for counting (user's bookings in guest view, all in full view)
  const baseBookingsForCounts = useMemo(() => {
    if (isGuestView && currentUser) {
      return bookings.filter(booking => booking.userId === currentUser.id);
    }
    return bookings;
  }, [bookings, isGuestView, currentUser]);

  // Convert bookings to calendar events
  const calendarEvents = useMemo(() => {
    return calendarFilteredBookings.map(booking => {
      const home = homes.find(h => h.id === booking.homeId);
      const user = users.find(u => u.id === booking.userId);

      return {
        id: booking.id,
        title: `${home?.name || 'Unknown'} - ${user?.name || 'Unknown'}`,
        start: booking.startDate,
        end: booking.endDate,
        resource: booking,
        className: booking.status,
      };
    });
  }, [calendarFilteredBookings, homes, users]);

  const getStatusVariant = (status: string): "default" | "destructive" | "outline" | "secondary" => {
    switch (status) {
      case 'approved': return 'default';
      case 'pending': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle size={16} />;
      case 'pending': return <AlertCircle size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      default: return <Eye size={16} />;
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('de-DE', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const calculateNights = (startDate: Date, endDate: Date) => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getHomeById = (homeId: string) => {
    return homes.find(home => home.id === homeId);
  };

  const getUserById = (userId: string) => {
    return users.find(user => user.id === userId);
  };

  const handleApprove = (bookingId: string) => {
    const updates: { status: 'approved'; totalPrice?: number } = { status: 'approved' };
    const override = priceOverrides[bookingId];
    if (override !== undefined && override !== '') {
      const parsed = parseFloat(override);
      if (!isNaN(parsed)) updates.totalPrice = parsed;
    }
    updateBooking(bookingId, updates);
    setPriceOverrides(prev => { const next = { ...prev }; delete next[bookingId]; return next; });
  };

  const handleCancel = (bookingId: string) => {
    const updates: { status: 'cancelled'; totalPrice?: number } = { status: 'cancelled' };
    const override = priceOverrides[bookingId];
    if (override !== undefined && override !== '') {
      const parsed = parseFloat(override);
      if (!isNaN(parsed)) updates.totalPrice = parsed;
    }
    updateBooking(bookingId, updates);
    setPriceOverrides(prev => { const next = { ...prev }; delete next[bookingId]; return next; });
  };

  const handleOpenEditModal = (booking: Booking) => {
    setEditingBooking(booking);
    setEditStartDate(moment(booking.startDate).format('YYYY-MM-DD'));
    setEditEndDate(moment(booking.endDate).format('YYYY-MM-DD'));
    setEditError(null);
    setEditAvailabilityStatus(null);
  };

  const handleCloseEditModal = () => {
    setEditingBooking(null);
    setEditStartDate('');
    setEditEndDate('');
    setEditError(null);
    setEditAvailabilityStatus(null);
  };

  const handleEditDateChange = async (newStartDate: string, newEndDate: string) => {
    setEditStartDate(newStartDate);
    setEditEndDate(newEndDate);
    setEditError(null);
    setEditAvailabilityStatus(null);

    if (newStartDate && newEndDate && editingBooking) {
      try {
        const result = await checkAvailability(
          editingBooking.homeId,
          new Date(newStartDate),
          new Date(newEndDate),
          editingBooking.id
        );
        if (result.available) {
          setEditAvailabilityStatus({ available: true, message: 'Termine sind verfügbar!' });
        } else {
          const message = result.blockedReason || 'Diese Termine sind nicht verfügbar';
          setEditAvailabilityStatus({ available: false, message });
        }
      } catch {
        setEditAvailabilityStatus({ available: false, message: 'Verfügbarkeit konnte nicht geprüft werden' });
      }
    }
  };

  const handleSubmitEdit = async () => {
    if (!editingBooking || !editStartDate || !editEndDate) return;

    setIsEditSubmitting(true);
    setEditError(null);

    const result = await editBooking(editingBooking.id, {
      startDate: new Date(editStartDate),
      endDate: new Date(editEndDate),
    });

    if (result.success) {
      handleCloseEditModal();
    } else {
      setEditError(result.error || 'Buchung konnte nicht aktualisiert werden');
    }

    setIsEditSubmitting(false);
  };

  const getEditNights = () => {
    if (!editStartDate || !editEndDate) return 0;
    const start = new Date(editStartDate);
    const end = new Date(editEndDate);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getEditPrice = () => {
    if (!editingBooking || !editStartDate || !editEndDate) return 0;
    const home = homes.find(h => h.id === editingBooking.homeId);
    if (!home) return 0;

    if (home.pricingStructure.seasonalConfig) {
      const nights = getEditNights();
      return Math.round(getWeeklyRate(new Date(editStartDate), home.pricingStructure.seasonalConfig) * nights);
    }

    const user = users.find(u => u.id === editingBooking.userId);
    const nights = getEditNights();
    const baseRate = user?.isFamily
      ? home.pricingStructure.userTypeOverrides?.family || home.pricingStructure.weekdayRate
      : home.pricingStructure.userTypeOverrides?.guest || home.pricingStructure.weekdayRate;
    return baseRate * nights;
  };

  const handleDateSelect = (slotInfo: { start: Date; end: Date; slots: Date[] }) => {
    const clickedDate = moment(slotInfo.start).format('YYYY-MM-DD');

    if (isSelectingRange && selectedStartDate) {
      const startMoment = moment(selectedStartDate);
      const clickedMoment = moment(clickedDate);

      if (clickedMoment.isBefore(startMoment)) {
        setSelectedEndDate(selectedStartDate);
        setSelectedStartDate(clickedDate);
      } else {
        setSelectedEndDate(clickedDate);
      }
      setIsSelectingRange(false);
    } else {
      setSelectedStartDate(clickedDate);
      setSelectedEndDate('');
      setIsSelectingRange(true);
    }
  };

  const handleConfirmBooking = async () => {
    if (!selectedStartDate || !selectedEndDate || !selectedHome || !currentUser) return;

    setIsSubmitting(true);
    setBookingError(null);

    const isFamilyMemberUser = !isOwner && Boolean(currentUser.isFamilyMember);
    const baseRequest = {
      userId: currentUser.id,
      startDate: new Date(selectedStartDate),
      endDate: new Date(selectedEndDate),
      cleaningOption: (isOwner || isFamilyMemberUser) ? cleaningOption : undefined,
    };

    const result = await createBooking({ ...baseRequest, homeId: selectedHome });

    if (result.success) {
      if (bookBothHomes && selectedHomes[1]) {
        await createBooking({ ...baseRequest, homeId: selectedHomes[1] });
      }
      handleClearSelection();
      if ((isOwner || currentUser.isFamilyMember) && familyBookingInfo) {
        setShowFamilyInfo(true);
      }
    } else {
      setBookingError(result.error || 'Buchung konnte nicht erstellt werden');
    }

    setIsSubmitting(false);
  };

  // Check availability when home(s) and dates change
  useEffect(() => {
    const checkDates = async () => {
      if (selectedStartDate && selectedEndDate && selectedHome) {
        setAvailabilityStatus(null);
        setOtherHomeAvailability(null);
        setBookingError(null);
        try {
          const result = await checkAvailability(
            selectedHome,
            new Date(selectedStartDate),
            new Date(selectedEndDate)
          );
          if (result.available) {
            setAvailabilityStatus({ available: true, message: 'Termine sind verfügbar!' });
          } else {
            setAvailabilityStatus({ available: false, message: result.blockedReason || 'Diese Termine sind nicht verfügbar' });
          }

          // Also check second home when both are selected
          if (bookBothHomes && selectedHomes[1]) {
            const secondHome = homes.find(h => h.id === selectedHomes[1]);
            const result2 = await checkAvailability(
              selectedHomes[1],
              new Date(selectedStartDate),
              new Date(selectedEndDate)
            );
            setOtherHomeAvailability({
              available: result2.available,
              message: result2.available
                ? `${secondHome?.name} ist verfügbar.`
                : result2.blockedReason || `${secondHome?.name} ist nicht verfügbar.`,
            });
          }
        } catch {
          setAvailabilityStatus({ available: false, message: 'Verfügbarkeit konnte nicht geprüft werden' });
        }
      } else {
        setAvailabilityStatus(null);
        setOtherHomeAvailability(null);
      }
    };
    checkDates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedEndDate, selectedHomes, checkAvailability]);

  // Close home dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (homeDropdownRef.current && !homeDropdownRef.current.contains(e.target as Node)) {
        setHomeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClearSelection = () => {
    setSelectedStartDate('');
    setSelectedEndDate('');
    setSelectedHomes([]);
    setIsSelectingRange(false);
    setAvailabilityStatus(null);
    setOtherHomeAvailability(null);
    setBookingError(null);
    setCleaningOption('self');
    setShowFamilyInfo(false);
  };

  const getTodayDate = () => {
    return moment().format('YYYY-MM-DD');
  };

  const getDayPropGetter = (date: Date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const classes = [];

    if (selectedStartDate && selectedEndDate) {
      const start = moment(selectedStartDate);
      const end = moment(selectedEndDate);
      const current = moment(date);

      if (dateStr === selectedStartDate) {
        classes.push('selected-start');
      } else if (dateStr === selectedEndDate) {
        classes.push('selected-end');
      } else if (current.isBetween(start, end, 'day', '[]')) {
        classes.push('selected-range');
      }
    } else if (dateStr === selectedStartDate) {
      classes.push('selected-single');
    }

    return { className: classes.join(' ') };
  };

  const filterTabs = [
    { key: 'all', label: isGuestView ? 'Meine Buchungen' : 'Alle Buchungen', count: baseBookingsForCounts.length, icon: <Eye size={16} /> },
    { key: 'pending', label: 'Ausstehend', count: baseBookingsForCounts.filter(b => b.status === 'pending').length, icon: <AlertCircle size={16} /> },
    { key: 'approved', label: 'Bestätigt', count: baseBookingsForCounts.filter(b => b.status === 'approved').length, icon: <CheckCircle size={16} /> },
    { key: 'cancelled', label: 'Storniert', count: baseBookingsForCounts.filter(b => b.status === 'cancelled').length, icon: <XCircle size={16} /> },
  ];

  const goToPreviousMonth = () => {
    setCurrentDate(prev => moment(prev).subtract(1, 'month').toDate());
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => moment(prev).add(1, 'month').toDate());
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get seasonal config for selected home
  const selectedHomeSeasonalConfig = useMemo(() => {
    if (!selectedHome) return null;
    const home = homes.find(h => h.id === selectedHome);
    return home?.pricingStructure.seasonalConfig || null;
  }, [selectedHome, homes]);

  useEffect(() => {
    if (!calendarRef.current) return;

    const timeoutId = setTimeout(() => {
      if (!calendarRef.current) return;

      const allDateCells = calendarRef.current.querySelectorAll('.rbc-day-bg');
      const allDateNumbers = calendarRef.current.querySelectorAll('.rbc-date-cell');

      allDateCells.forEach((cell) => {
        cell.classList.remove('selected-start', 'selected-end', 'selected-range', 'selected-single', 'high-season', 'low-season');
        (cell as HTMLElement).style.backgroundColor = '';
        (cell as HTMLElement).style.border = '';
        (cell as HTMLElement).style.borderRadius = '';
      });

      allDateNumbers.forEach((cell) => {
        const dateButton = cell.querySelector('.rbc-button-link') as HTMLElement;
        if (dateButton) {
          dateButton.style.backgroundColor = '';
          dateButton.style.backgroundImage = '';
          dateButton.style.color = '';
          dateButton.style.borderRadius = '';
          dateButton.style.padding = '';
          dateButton.style.fontWeight = '';
          dateButton.style.display = '';
          dateButton.style.width = '';
          dateButton.style.height = '';
          dateButton.style.lineHeight = '';
          dateButton.style.textAlign = '';
          dateButton.style.boxShadow = '';
        }
      });

      // Apply season colors when a property is selected
      if (selectedHomeSeasonalConfig) {
        const monthRows = calendarRef.current.querySelectorAll('.rbc-month-row');

        monthRows.forEach((row) => {
          const bgRow = row.querySelector('.rbc-row-bg');
          const contentRow = row.querySelector('.rbc-row-content');

          if (!bgRow || !contentRow) return;

          const bgCells = bgRow.querySelectorAll('.rbc-day-bg');
          const dateCells = contentRow.querySelectorAll('.rbc-date-cell');

          bgCells.forEach((bgCell, index) => {
            const dateCell = dateCells[index];
            if (!dateCell) return;

            const dateButton = dateCell.querySelector('.rbc-button-link') as HTMLElement;
            if (!dateButton) return;

            const dayText = dateButton.textContent?.trim();
            if (!dayText) return;

            const day = parseInt(dayText);
            if (isNaN(day)) return;

            const isOffRange = dateCell.classList.contains('rbc-off-range');
            let cellDate: moment.Moment;

            if (isOffRange) {
              if (day > 15) {
                cellDate = moment(currentDate).subtract(1, 'month').date(day);
              } else {
                cellDate = moment(currentDate).add(1, 'month').date(day);
              }
            } else {
              cellDate = moment(currentDate).date(day);
            }

            // Apply season colors (only if not selected)
            const dateStr = cellDate.format('YYYY-MM-DD');
            const isSelected = dateStr === selectedStartDate || dateStr === selectedEndDate;

            if (!isSelected) {
              const seasonInfo = getSeasonForDate(cellDate.toDate(), selectedHomeSeasonalConfig);
              if (seasonInfo.type === 'high') {
                (bgCell as HTMLElement).style.backgroundColor = 'rgba(236, 72, 153, 0.1)'; // pink-500
                bgCell.classList.add('high-season');
              } else {
                (bgCell as HTMLElement).style.backgroundColor = 'rgba(6, 182, 212, 0.1)'; // cyan-500
                bgCell.classList.add('low-season');
              }
            }
          });
        });
      }

      if (selectedStartDate || selectedEndDate) {
        const monthRows = calendarRef.current.querySelectorAll('.rbc-month-row');

        monthRows.forEach((row) => {
          const bgRow = row.querySelector('.rbc-row-bg');
          const contentRow = row.querySelector('.rbc-row-content');

          if (!bgRow || !contentRow) return;

          const bgCells = bgRow.querySelectorAll('.rbc-day-bg');
          const dateCells = contentRow.querySelectorAll('.rbc-date-cell');

          bgCells.forEach((bgCell, index) => {
            const dateCell = dateCells[index];
            if (!dateCell) return;

            const dateButton = dateCell.querySelector('.rbc-button-link') as HTMLElement;
            if (!dateButton) return;

            const dayText = dateButton.textContent?.trim();
            if (!dayText) return;

            const day = parseInt(dayText);
            if (isNaN(day)) return;

            const isOffRange = dateCell.classList.contains('rbc-off-range');

            let cellDate: moment.Moment;

            if (isOffRange) {
              if (day > 15) {
                cellDate = moment(currentDate).subtract(1, 'month').date(day);
              } else {
                cellDate = moment(currentDate).add(1, 'month').date(day);
              }
            } else {
              cellDate = moment(currentDate).date(day);
            }

            const dateStr = cellDate.format('YYYY-MM-DD');

            if (selectedStartDate && selectedEndDate) {
              const start = moment(selectedStartDate);
              const end = moment(selectedEndDate);

              if (dateStr === selectedStartDate) {
                bgCell.classList.add('selected-start');
                (bgCell as HTMLElement).style.backgroundColor = 'rgba(99, 102, 241, 0.15)';
                (bgCell as HTMLElement).style.border = '2px solid #6366f1';
                (bgCell as HTMLElement).style.borderRadius = '12px';

                dateButton.style.backgroundImage = 'linear-gradient(to right, #6366f1, #a855f7)';
                dateButton.style.color = 'white';
                dateButton.style.borderRadius = '50%';
                dateButton.style.padding = '2px';
                dateButton.style.fontWeight = '700';
                dateButton.style.display = 'inline-block';
                dateButton.style.width = '28px';
                dateButton.style.height = '28px';
                dateButton.style.lineHeight = '24px';
                dateButton.style.textAlign = 'center';
                dateButton.style.boxShadow = '0 4px 10px rgba(99, 102, 241, 0.4)';
              } else if (dateStr === selectedEndDate) {
                bgCell.classList.add('selected-end');
                (bgCell as HTMLElement).style.backgroundColor = 'rgba(168, 85, 247, 0.15)';
                (bgCell as HTMLElement).style.border = '2px solid #a855f7';
                (bgCell as HTMLElement).style.borderRadius = '12px';

                dateButton.style.backgroundImage = 'linear-gradient(to right, #a855f7, #ec4899)';
                dateButton.style.color = 'white';
                dateButton.style.borderRadius = '50%';
                dateButton.style.padding = '2px';
                dateButton.style.fontWeight = '700';
                dateButton.style.display = 'inline-block';
                dateButton.style.width = '28px';
                dateButton.style.height = '28px';
                dateButton.style.lineHeight = '24px';
                dateButton.style.textAlign = 'center';
                dateButton.style.boxShadow = '0 4px 10px rgba(168, 85, 247, 0.4)';
              } else if (cellDate.isBetween(start, end, 'day', '()')) {
                bgCell.classList.add('selected-range');
                (bgCell as HTMLElement).style.backgroundColor = 'rgba(99, 102, 241, 0.08)';
              }
            } else if (dateStr === selectedStartDate) {
              bgCell.classList.add('selected-single');
              (bgCell as HTMLElement).style.backgroundColor = 'rgba(99, 102, 241, 0.15)';
              (bgCell as HTMLElement).style.border = '2px solid #6366f1';
              (bgCell as HTMLElement).style.borderRadius = '12px';

              dateButton.style.backgroundImage = 'linear-gradient(to right, #6366f1, #a855f7)';
              dateButton.style.color = 'white';
              dateButton.style.borderRadius = '50%';
              dateButton.style.padding = '2px';
              dateButton.style.fontWeight = '700';
              dateButton.style.display = 'inline-block';
              dateButton.style.width = '28px';
              dateButton.style.height = '28px';
              dateButton.style.lineHeight = '24px';
              dateButton.style.textAlign = 'center';
              dateButton.style.boxShadow = '0 4px 10px rgba(99, 102, 241, 0.4)';
            }
          });
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedStartDate, selectedEndDate, currentDate, selectedHomeSeasonalConfig]);

  return (
    <div className="container mx-auto p-4 sm:p-6 max-w-7xl">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Buchungskalender</h1>
        <p className="text-muted-foreground">Alle Buchungen im Kalender- und Tabellenformat anzeigen und verwalten</p>
      </div>

      <div className="flex gap-2 mb-6 border-b border-border pb-2 overflow-x-auto -mx-6 px-6 sm:mx-0 sm:px-0 scrollbar-thin">
        {filterTabs.map((tab) => (
          <button
            key={tab.key}
            className={`px-3 sm:px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors border-b-2 whitespace-nowrap flex-shrink-0 ${
              filter === tab.key
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-primary hover:bg-accent'
            }`}
            onClick={() => setFilter(tab.key as any)}
          >
            {tab.icon}
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <section className="mb-8">
        <Card ref={calendarRef}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon size={20} />
              Kalenderübersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-6 p-4 bg-muted rounded-lg border border-border space-y-4">
              {/* Row 1: Date inputs side by side */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Anreise</label>
                  <Input
                    type="date"
                    value={selectedStartDate}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      setSelectedStartDate(newStartDate);
                      if (selectedEndDate && moment(selectedEndDate).isBefore(moment(newStartDate))) {
                        setSelectedEndDate('');
                      }
                    }}
                    min={getTodayDate()}
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Abreise</label>
                  <Input
                    type="date"
                    value={selectedEndDate}
                    onChange={(e) => {
                      const newEndDate = e.target.value;
                      if (selectedStartDate && moment(newEndDate).isBefore(moment(selectedStartDate))) {
                        setSelectedEndDate(selectedStartDate);
                        setSelectedStartDate(newEndDate);
                      } else {
                        setSelectedEndDate(newEndDate);
                      }
                    }}
                    min={selectedStartDate || getTodayDate()}
                  />
                </div>
              </div>

              {/* Row 2: Home selector + action buttons */}
              <div className="flex gap-3 items-end">
                <div className="flex-1 space-y-1.5">
                  <label className="text-sm font-medium">Unterkunft</label>
                  {isOwner ? (
                    <div className="relative" ref={homeDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setHomeDropdownOpen(o => !o)}
                        className="w-full h-10 flex items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-muted transition-colors"
                      >
                        <span className={selectedHomes.length === 0 ? 'text-muted-foreground' : ''}>
                          {selectedHomes.length === 0
                            ? 'Unterkunft auswählen'
                            : selectedHomes.length === 1
                              ? homes.find(h => h.id === selectedHomes[0])?.name
                              : `${selectedHomes.length} Unterkünfte gewählt`}
                        </span>
                        <ChevronDown size={14} className={`ml-2 shrink-0 transition-transform ${homeDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {homeDropdownOpen && (
                        <div className="absolute top-full mt-1 z-50 w-full rounded-md border border-input bg-background shadow-lg">
                          {homes.map(home => (
                            <label
                              key={home.id}
                              className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-muted cursor-pointer text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={selectedHomes.includes(home.id)}
                                onChange={(e) => {
                                  setSelectedHomes(prev =>
                                    e.target.checked
                                      ? [...prev, home.id]
                                      : prev.filter(id => id !== home.id)
                                  );
                                }}
                                className="h-4 w-4 rounded border-input"
                              />
                              {home.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <select
                      value={selectedHome}
                      onChange={(e) => setSelectedHomes(e.target.value ? [e.target.value] : [])}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">Unterkunft auswählen</option>
                      {homes.map(home => (
                        <option key={home.id} value={home.id}>{home.name}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="flex gap-2 shrink-0">
                  <Button
                    onClick={handleConfirmBooking}
                    disabled={
                      !selectedStartDate || !selectedEndDate || !selectedHome || isSubmitting ||
                      (availabilityStatus !== null && !availabilityStatus.available) ||
                      (bookBothHomes && otherHomeAvailability !== null && !otherHomeAvailability.available)
                    }
                    className="flex items-center gap-2"
                  >
                    <Plus size={16} />
                    {isSubmitting ? 'Wird gebucht...' : bookBothHomes ? 'Beide buchen' : 'Buchen'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleClearSelection}
                    size="icon"
                    title="Zurücksetzen"
                  >
                    <RotateCcw size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Cleaning option — only for Eigentümer and Familienmitglieder */}
            {(isOwner || currentUser?.isFamilyMember) && selectedHome && (() => {
              const home = homes.find(h => h.id === selectedHome);
              const fee = home?.structuredContent?.paymentTerms?.cleaningFee ?? 0;
              if (fee <= 0) return null;
              return (
                <div className="flex flex-wrap items-center gap-4 px-4 py-3 rounded-md border bg-muted/40">
                  <Label className="text-sm font-medium shrink-0">Reinigung</Label>
                  <RadioGroup
                    value={cleaningOption}
                    onValueChange={(v) => setCleaningOption(v as 'self' | 'service')}
                    className="flex gap-4"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="self" id="bp-clean-self" />
                      <label htmlFor="bp-clean-self" className="text-sm cursor-pointer">Ich reinige selbst</label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="service" id="bp-clean-service" />
                      <label htmlFor="bp-clean-service" className="text-sm cursor-pointer">
                        Reinigungsdienst <span className="text-muted-foreground">(+CHF {fee})</span>
                      </label>
                    </div>
                  </RadioGroup>
                </div>
              );
            })()}

            {/* Availability Status */}
            {availabilityStatus && (
              <div className={`flex items-center gap-2 px-4 py-3 rounded-md mb-2 ${
                availabilityStatus.available
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-500'
                  : 'bg-rose-500/10 border border-rose-500/20 text-rose-500'
              }`}>
                {availabilityStatus.available ? <CheckCircle size={16} /> : <XCircle size={16} />}
                <span className="text-sm font-medium">{availabilityStatus.message}</span>
              </div>
            )}
            {bookBothHomes && otherHomeAvailability && (
              <div className={`flex items-center gap-2 px-4 py-3 rounded-md mb-4 ${
                otherHomeAvailability.available
                  ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-500'
                  : 'bg-rose-500/10 border border-rose-500/20 text-rose-500'
              }`}>
                {otherHomeAvailability.available ? <CheckCircle size={16} /> : <XCircle size={16} />}
                <span className="text-sm font-medium">{otherHomeAvailability.message}</span>
              </div>
            )}

            {/* Booking Error */}
            {bookingError && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-md mb-4 bg-rose-500/10 border border-rose-500/20 text-rose-500">
                <XCircle size={16} />
                <span className="text-sm font-medium">{bookingError}</span>
              </div>
            )}

            {/* Post-booking success notice */}
            {showFamilyInfo && familyBookingInfo && (
              <div className="flex items-center justify-between px-4 py-3 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-emerald-700">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <CheckCircle size={16} />
                  Buchung eingereicht! Scroll nach unten für wichtige Infos zum Aufenthalt.
                </div>
                <button type="button" onClick={() => setShowFamilyInfo(false)} className="text-emerald-600 hover:text-emerald-800 text-lg leading-none">×</button>
              </div>
            )}

            {/* Price Preview */}
            {selectedStartDate && selectedEndDate && selectedHome && availabilityStatus?.available && (() => {
              const home = homes.find(h => h.id === selectedHome);
              if (!home) return null;
              const price = home.pricingStructure.seasonalConfig
                ? getWeeklyRate(new Date(selectedStartDate), home.pricingStructure.seasonalConfig)
                : home.pricingStructure.weekdayRate * 7;
              const season = home.pricingStructure.seasonalConfig
                ? getSeasonForDate(new Date(selectedStartDate), home.pricingStructure.seasonalConfig)
                : null;
              return (
                <div className="flex items-center justify-between px-4 py-3 rounded-md mb-4 bg-muted border border-border">
                  <div className="text-sm text-muted-foreground">
                    {season ? (season.type === 'high' ? 'Hauptsaison' : 'Nebensaison') : 'Nachtpreis'}
                  </div>
                  <div className="font-semibold text-lg">CHF {price}</div>
                </div>
              );
            })()}

            {/* Payment Hint */}
            {selectedStartDate && selectedEndDate && selectedHome && availabilityStatus?.available && (
              <BookingHint className="mb-4" />
            )}

            {(selectedStartDate || selectedEndDate || isSelectingRange) && (
              <div className={`flex items-center gap-2 px-4 py-3 rounded-md mb-6 ${
                isSelectingRange
                  ? 'bg-purple-500/10 border border-purple-500/20 text-purple-500'
                  : 'bg-indigo-500/10 border border-indigo-500/20 text-indigo-500'
              }`}>
                <Clock size={16} />
                <span className="text-sm font-medium">
                  {isSelectingRange
                    ? `Abreisedatum wählen (Anreise: ${moment(selectedStartDate).format('D. MMM YYYY')})`
                    : selectedStartDate && selectedEndDate
                      ? `Ausgewählt: ${moment(selectedStartDate).format('D. MMM YYYY')} – ${moment(selectedEndDate).format('D. MMM YYYY')}`
                      : 'Datum anklicken um Zeitraum zu wählen'
                  }
                </span>
              </div>
            )}

            <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
              <div className="flex gap-2 order-1">
                <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
                  <ChevronLeft size={16} />
                </Button>
                <Button variant="outline" size="icon" onClick={goToNextMonth}>
                  <ChevronRight size={16} />
                </Button>
              </div>
              <div className="text-lg sm:text-xl font-semibold text-primary order-3 sm:order-2 w-full sm:w-auto text-center">
                {moment(currentDate).format('MMMM YYYY')}
              </div>
              <Button variant="secondary" onClick={goToToday} className="order-2 sm:order-3">
                Heute
              </Button>
            </div>

            <div className="calendar-wrapper">
              <Calendar
                localizer={localizer}
                events={calendarEvents}
                startAccessor="start"
                endAccessor="end"
                style={{ height: 500 }}
                views={['month']}
                defaultView="month"
                date={currentDate}
                onNavigate={setCurrentDate}
                onSelectSlot={handleDateSelect}
                selectable={true}
                dayPropGetter={getDayPropGetter}
                eventPropGetter={(event) => ({
                  className: event.className,
                })}
                toolbar={false}
              />
            </div>

            {/* Season Legend - shows when a property is selected */}
            {selectedHome && (() => {
              const home = homes.find(h => h.id === selectedHome);
              if (!home?.pricingStructure.seasonalConfig) return null;
              return (
                <div className="mt-4 p-3 bg-muted rounded-lg border border-border">
                  <div className="text-xs font-medium text-muted-foreground mb-2">Saisonlegende</div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-pink-500/10 border border-pink-500/30" />
                      <span className="text-sm">Hochsaison</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded bg-cyan-500/10 border border-cyan-500/30" />
                      <span className="text-sm">Nebensaison</span>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      </section>

      {/* Bankverbindung — für Gäste mit ausstehenden, unbezahlten Buchungen */}
      {isGuestView && tableFilteredBookings.some(b => !b.isPaid && b.status !== 'cancelled') && (
        <section className="mb-6">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Zahlung</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                Bitte überweisen Sie den Betrag auf folgendes Konto:
              </p>
              <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1.5 text-sm">
                <span className="text-muted-foreground">Inhaber</span>
                <span className="font-medium">Yvo Tobias, Lucas Patrick &amp; Dominik Michael Richner</span>
                <span className="text-muted-foreground">Konto</span>
                <span>Privatkonto CHF – Rte du Lac 37, Môtier</span>
                <span className="text-muted-foreground">IBAN</span>
                <span className="font-mono">CH85 0076 9434 1800 7200 2</span>
                <span className="text-muted-foreground">BIC</span>
                <span className="font-mono">BLKBCH22</span>
                <span className="text-muted-foreground">Verwendungszweck</span>
                <span>Buchungsnummer (z.B. {new Date().getFullYear()}-123456)</span>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section className="mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter size={20} />
              {isGuestView ? 'Meine Buchungen' : 'Buchungsliste'}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{tableFilteredBookings.length} Buchungen</p>
          </CardHeader>
          <CardContent className="p-0">
            {tableFilteredBookings.length > 0 ? (() => {
              const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
              const grouped = tableFilteredBookings.reduce<Record<string, typeof tableFilteredBookings>>((acc, booking) => {
                const d = new Date(booking.startDate);
                const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                if (!acc[key]) acc[key] = [];
                acc[key].push(booking);
                return acc;
              }, {});
              const sortedKeys = Object.keys(grouped).sort();

              const renderBookingActions = (booking: Booking) => (
                <div className="flex flex-wrap gap-2">
                  {currentUser?.isAdmin && booking.status === 'pending' && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleApprove(booking.id)}
                        className="flex items-center gap-1"
                      >
                        <CheckCircle size={14} />
                        Genehmigen
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCancel(booking.id)}
                        className="flex items-center gap-1"
                      >
                        <XCircle size={14} />
                        Ablehnen
                      </Button>
                    </>
                  )}
                  {booking.userId === currentUser?.id && booking.status === 'pending' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEditModal(booking)}
                        className="flex items-center gap-1"
                      >
                        <Pencil size={14} />
                        Bearbeiten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => cancelBooking(booking.id)}
                        className="flex items-center gap-1"
                      >
                        <XCircle size={14} />
                        Stornieren
                      </Button>
                    </>
                  )}
                  {!isGuestView && currentUser?.isAdmin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeletingBooking(booking)}
                      className="flex items-center gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              );

              const renderBookingPrice = (booking: Booking) => (
                currentUser?.isAdmin && booking.status === 'pending' ? (
                  <div className="flex items-center gap-1">
                    <Banknote size={14} className="text-primary shrink-0" />
                    <span className="text-sm text-muted-foreground">CHF</span>
                    <Input
                      type="number"
                      min={0}
                      className="h-7 w-24 text-sm font-semibold"
                      value={priceOverrides[booking.id] ?? booking.totalPrice}
                      onChange={e => setPriceOverrides(prev => ({ ...prev, [booking.id]: e.target.value }))}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-0.5">
                    <div className="font-semibold text-primary flex items-center gap-1">
                      <Banknote size={14} />
                      CHF {booking.totalPrice}
                      {booking.totalPrice === 0 && (() => {
                        const booker = users.find(u => u.id === booking.userId);
                        const label = (booker?.isAdmin || booker?.isFamily) ? 'Eigentümer' : booker?.isFamilyMember ? 'Familienmitglied' : null;
                        return label ? <span className="text-xs font-normal text-muted-foreground ml-1">{label}</span> : null;
                      })()}
                    </div>
                    {booking.listPrice != null && (currentUser?.isAdmin || currentUser?.isFamily) && (
                      <div className="text-xs text-muted-foreground line-through">
                        Listenpreis: CHF {booking.listPrice}
                      </div>
                    )}
                    {booking.cleaningOption && (
                      <div className="text-xs text-muted-foreground">
                        {booking.cleaningOption === 'self' ? 'Selbstreinigung' : 'Reinigungsdienst'}
                      </div>
                    )}
                  </div>
                )
              );

              const renderBookingPayment = (booking: Booking) => (
                currentUser?.isAdmin ? (
                  <Button
                    variant={booking.isPaid ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => updateBooking(booking.id, { isPaid: !booking.isPaid })}
                    className={booking.isPaid ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
                  >
                    {booking.isPaid ? 'Bezahlt' : 'Als bezahlt markieren'}
                  </Button>
                ) : (
                  <Badge variant={booking.isPaid ? 'default' : 'secondary'}>
                    {booking.isPaid ? 'Bezahlt' : 'Ausstehend'}
                  </Badge>
                )
              );

              return (
                <>
                  {/* Mobile: Card Layout */}
                  <div className="md:hidden divide-y divide-border">
                    {sortedKeys.map((monthKey) => {
                      const [year, month] = monthKey.split('-');
                      const monthLabel = `${MONTHS[parseInt(month) - 1]} ${year}`;
                      const monthBookings = grouped[monthKey];
                      return (
                        <div key={monthKey}>
                          <div className="bg-muted/60 px-4 py-2">
                            <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{monthLabel}</span>
                            <span className="ml-2 text-xs text-muted-foreground">({monthBookings.length})</span>
                          </div>
                          {monthBookings.map((booking) => {
                            const home = getHomeById(booking.homeId);
                            const user = getUserById(booking.userId);
                            const nights = calculateNights(booking.startDate, booking.endDate);
                            return (
                              <div key={booking.id} className="p-4 space-y-3 border-b border-border last:border-b-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div>
                                    <div className="font-semibold text-primary">{home?.name || 'Unbekannte Unterkunft'}</div>
                                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                                      <User size={14} />
                                      {user?.name || 'Unbekannter Benutzer'}
                                    </div>
                                  </div>
                                  <Badge variant={getStatusVariant(booking.status)} className="flex items-center gap-1 shrink-0">
                                    {getStatusIcon(booking.status)}
                                    {booking.status}
                                  </Badge>
                                </div>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1 font-mono">
                                    <Hash size={12} />
                                    {booking.referenceNumber || '-'}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock size={12} />
                                    {formatDate(booking.startDate)} – {formatDate(booking.endDate)}
                                  </span>
                                  <span>{nights} Nächte</span>
                                </div>
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  {renderBookingPrice(booking)}
                                  {renderBookingPayment(booking)}
                                </div>
                                {renderBookingActions(booking)}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop: Table Layout */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Referenz</TableHead>
                          <TableHead>Unterkunft & Gast</TableHead>
                          <TableHead>Daten</TableHead>
                          <TableHead>Dauer</TableHead>
                          <TableHead>Preis</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Zahlung</TableHead>
                          <TableHead>Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {sortedKeys.map((monthKey) => {
                          const [year, month] = monthKey.split('-');
                          const monthLabel = `${MONTHS[parseInt(month) - 1]} ${year}`;
                          const monthBookings = grouped[monthKey];
                          return (
                            <React.Fragment key={monthKey}>
                              <TableRow className="bg-muted/60 hover:bg-muted/60">
                                <TableCell colSpan={8} className="py-2 px-4">
                                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">{monthLabel}</span>
                                  <span className="ml-2 text-xs text-muted-foreground">({monthBookings.length} {monthBookings.length === 1 ? 'Buchung' : 'Buchungen'})</span>
                                </TableCell>
                              </TableRow>
                              {monthBookings.map((booking) => {
                                const home = getHomeById(booking.homeId);
                                const user = getUserById(booking.userId);
                                const nights = calculateNights(booking.startDate, booking.endDate);
                                return (
                                  <TableRow key={booking.id}>
                                    <TableCell>
                                      <div className="flex items-center gap-1 font-mono text-sm">
                                        <Hash size={14} className="text-muted-foreground" />
                                        {booking.referenceNumber || '-'}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="font-semibold text-primary">{home?.name || 'Unbekannte Unterkunft'}</div>
                                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                                        <User size={14} />
                                        {user?.name || 'Unbekannter Benutzer'}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2 text-sm">
                                        <Clock size={14} />
                                        <div>
                                          <div>{formatDate(booking.startDate)}</div>
                                          <div>{formatDate(booking.endDate)}</div>
                                        </div>
                                      </div>
                                    </TableCell>
                                    <TableCell>{nights} Nächte</TableCell>
                                    <TableCell>{renderBookingPrice(booking)}</TableCell>
                                    <TableCell>
                                      <Badge variant={getStatusVariant(booking.status)} className="flex items-center gap-1 w-fit">
                                        {getStatusIcon(booking.status)}
                                        {booking.status}
                                      </Badge>
                                    </TableCell>
                                    <TableCell>{renderBookingPayment(booking)}</TableCell>
                                    <TableCell>{renderBookingActions(booking)}</TableCell>
                                  </TableRow>
                                );
                              })}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              );
            })() : (
              <div className="text-center py-16 px-4">
                <h3 className="text-xl font-semibold mb-2">Keine Buchungen gefunden</h3>
                <p className="text-muted-foreground">
                  {filter === 'all'
                    ? isGuestView
                      ? 'Du hast noch keine Buchungen. Stöbere durch die Unterkünfte für deine erste Buchung!'
                      : 'Es wurden noch keine Buchungen vorgenommen.'
                    : `Keine ${filter === 'pending' ? 'ausstehenden' : filter === 'approved' ? 'bestätigten' : 'stornierten'} Buchungen gefunden.`
                  }
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* Family booking info — persistent, toggleable for owners & family members */}
      {(isOwner || currentUser?.isFamilyMember) && familyBookingInfo && (
        <FamilyInfoPanel info={familyBookingInfo} />
      )}

      {/* Edit Booking Modal */}
      <Dialog open={!!editingBooking} onOpenChange={() => handleCloseEditModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Buchung bearbeiten</DialogTitle>
            <DialogDescription>
              {editingBooking && (
                <>
                  <span className="font-medium">{homes.find(h => h.id === editingBooking.homeId)?.name}</span>
                  {' - '}
                  <span className="font-mono text-xs">{editingBooking.referenceNumber}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-check-in">Anreisedatum</Label>
              <Input
                id="edit-check-in"
                type="date"
                value={editStartDate}
                onChange={(e) => handleEditDateChange(e.target.value, editEndDate)}
                min={getTodayDate()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-check-out">Abreisedatum</Label>
              <Input
                id="edit-check-out"
                type="date"
                value={editEndDate}
                onChange={(e) => handleEditDateChange(editStartDate, e.target.value)}
                min={editStartDate || getTodayDate()}
              />
            </div>

            {/* Availability Status */}
            {editAvailabilityStatus && (
              <div className={`text-sm p-3 rounded-md ${
                editAvailabilityStatus.available
                  ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                  : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'
              }`}>
                {editAvailabilityStatus.message}
              </div>
            )}

            {/* Edit Error */}
            {editError && (
              <div className="text-sm p-3 rounded-md bg-rose-500/10 text-rose-500 border border-rose-500/20">
                {editError}
              </div>
            )}

            {editStartDate && editEndDate && editAvailabilityStatus?.available && (
              <>
                <div className="bg-muted rounded-md p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{getEditNights()} Nächte</span>
                    <span>Aktualisierter Preis</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>Neuer Gesamtpreis</span>
                    <span>{getEditPrice()} €</span>
                  </div>
                </div>
                <BookingHint />
              </>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={handleCloseEditModal}>
              Abbrechen
            </Button>
            <Button
              onClick={handleSubmitEdit}
              disabled={!editStartDate || !editEndDate || isEditSubmitting || !editAvailabilityStatus?.available}
            >
              {isEditSubmitting ? 'Wird gespeichert...' : 'Änderungen speichern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingBooking} onOpenChange={() => !isDeleting && setDeletingBooking(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Buchung löschen</DialogTitle>
            <DialogDescription>
              Buchung <span className="font-mono font-semibold">#{deletingBooking?.referenceNumber}</span> von{' '}
              <span className="font-semibold">{users.find(u => u.id === deletingBooking?.userId)?.name}</span> wirklich dauerhaft löschen?
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeletingBooking(null)} disabled={isDeleting}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting}
              onClick={async () => {
                if (!deletingBooking) return;
                setIsDeleting(true);
                await deleteBooking(deletingBooking.id);
                setIsDeleting(false);
                setDeletingBooking(null);
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {isDeleting ? 'Wird gelöscht...' : 'Endgültig löschen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BookingsPage;
